/**
 * Tracking Plugin — Invisible ops portal usage analytics
 *
 * Hooks into Vue Router afterEach to capture page views.
 * Batches events and flushes via native fetch() (same pattern as
 * reportErrorSilently in api.ts — avoids axios interceptor re-entry).
 *
 * Also exports a `track()` function for granular interaction tracking
 * (clicks, field edits, orders, etc.) from any component.
 *
 * Staff users cannot see any of this; only admin can query the data.
 */
import type { Plugin, App } from 'vue'
import type { Router, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Absolute URL for tracking (native fetch, not axios)
const TRACKING_URL = import.meta.env.DEV
  ? '/api/v1/tracking/events'
  : 'https://api.claimshieldpro.com/api/v1/tracking/events'

const FLUSH_INTERVAL_MS = 30_000 // 30 seconds
const MAX_QUEUE_SIZE = 50
/* Below this, an unload is a reload artifact rather than a visit. See the
 * guard in the beforeunload handler for the incident this encodes. */
const MIN_UNLOAD_DWELL_MS = 1_000

interface TrackingEvent {
  event_type: string
  route_path: string
  route_name: string | null
  metadata: Record<string, unknown> | null
  duration_ms: number | null
  session_id: string
  timestamp: string
}

interface TrackingClick {
  route_path: string
  route_name: string | null
  x_pct: number
  y_px: number
  viewport_w: number
  viewport_h: number
  track_id: string | null
  element_path: string
  session_id: string
  timestamp: string
}

const MAX_PATH_DEPTH = 4
const MAX_CLICKS_PER_FLUSH = 100
/* Mirrors app/services/analytics/element_grammar.py. A path this builder
 * produces that the server's grammar refuses is a bug HERE, not a reason to
 * loosen the grammar. */
const TRACK_ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/

/**
 * Identify a clicked element WITHOUT reading any page content.
 *
 * The rule this encodes: `button[aria-label="John Smith"]` is a perfectly
 * ordinary CSS selector and a PII leak. So the builder never reads an
 * attribute, an id, a class, or text — only tag names, sibling positions, and
 * a `data-track` value, which is always a literal written in our own source.
 */
export function elementReference(el: Element | null): {
  trackId: string | null
  path: string
} {
  const segments: string[] = []
  let trackId: string | null = null
  let node: Element | null = el
  let depth = 0

  while (node && depth < MAX_PATH_DEPTH) {
    if (trackId === null) {
      const raw = node.getAttribute('data-track')
      if (raw && TRACK_ID_RE.test(raw)) trackId = raw
    }
    const tag = node.tagName.toLowerCase()
    const parent: Element | null = node.parentElement
    if (parent) {
      const current = node
      const sameTag = Array.from(parent.children).filter((c) => c.tagName === current.tagName)
      const idx = sameTag.indexOf(current) + 1
      segments.unshift(sameTag.length > 1 ? `${tag}:nth-of-type(${idx})` : tag)
    } else {
      segments.unshift(tag)
    }
    node = parent
    depth++
  }

  return { trackId, path: segments.join(' > ').slice(0, 255) }
}

// Module-level state shared between plugin and track()
let _queue: TrackingEvent[] = []
let _clicks: TrackingClick[] = []
let _app: 'ops' | 'portal' = 'ops'
let _sessionId: string | null = null
let _router: Router | null = null

/**
 * Get or create a per-browser-session UUID.
 * Stored in sessionStorage so it persists across navigations
 * but resets when the tab/window closes.
 */
function getSessionId(): string {
  const KEY = 'cs_tracking_session'
  let id = sessionStorage.getItem(KEY)
  if (!id) {
    id = crypto.randomUUID()
    sessionStorage.setItem(KEY, id)
  }
  return id
}

function getToken(): string | null {
  try {
    const auth = useAuthStore()
    return auth.token
  } catch {
    return null
  }
}

function isAuthenticated(): boolean {
  try {
    const auth = useAuthStore()
    return !!auth.token && !!auth.user
  } catch {
    return false
  }
}

/**
 * Flush queued events to the backend.
 * Uses native fetch with keepalive to survive page unloads.
 */
function flush(queue: TrackingEvent[], token: string | null): void {
  if ((queue.length === 0 && _clicks.length === 0) || !token) return

  const events = queue.splice(0) // drain the queue
  const clicks = _clicks.splice(0)

  try {
    fetch(TRACKING_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events, clicks, app: _app }),
      keepalive: true,
    })
    // Fire-and-forget — no .then()/.catch()
  } catch {
    // Swallow synchronous errors (serialization, etc.)
  }
}

/**
 * Track a custom interaction event from any component.
 *
 * Usage:
 *   import { track } from '@claimshield/shared'
 *   track('client_selected', { client_id: '...' })
 *   track('field_saved', { field_type: 'ssn', driver_id: '...' })
 *
 * ⚠ METADATA KEYS ARE A CLOSED SET, AND A REJECTED KEY IS SILENT.
 * The server validates every key against a registry
 * (`app/services/analytics/metadata_registry.py`) and 422s the WHOLE request
 * if one is unknown. `flush()` above drains the queue with `splice(0)` before
 * the fetch and has no `.catch()`, so a 422 does not retry, does not warn, and
 * does not appear anywhere — the entire batch is simply gone.
 *
 * Pass IDS, never names. `client_name`, `driver_name`, `employee_name`,
 * `staff_name`, `course_name`, `file_name`, `folder_name` and `query` were
 * retired from the registry on 2026-08-24 because they carried user content
 * into a table whose invariant is that no page content is stored. This example
 * taught `client_name` until 2026-08-26, which meant copying it cost the
 * copier their whole batch with no error anywhere. If you need a name, send
 * the id and resolve it in the database, where it is governed.
 */
export function track(eventType: string, metadata?: Record<string, unknown>): void {
  if (!isAuthenticated() || !_sessionId) return

  const route = _router?.currentRoute.value

  const event: TrackingEvent = {
    event_type: eventType,
    route_path: route?.path ?? '/',
    route_name: (route?.name as string) ?? null,
    metadata: metadata ?? null,
    duration_ms: null,
    session_id: _sessionId,
    timestamp: new Date().toISOString(),
  }

  _queue.push(event)

  if (_queue.length >= MAX_QUEUE_SIZE) {
    flush(_queue, getToken())
  }
}

export const trackingPlugin: Plugin = {
  install(
    _vueApp: App,
    { router, app = 'ops' }: { router: Router; app?: 'ops' | 'portal' },
  ) {
    const sessionId = getSessionId()
    _sessionId = sessionId
    _router = router
    _app = app
    _clicks = []
    const queue: TrackingEvent[] = []
    _queue = queue
    let lastNavTime: number = Date.now()
    let flushTimer: ReturnType<typeof setInterval> | null = null
    // Set on the first completed navigation. Distinguishes a real session from
    // a page that was destroyed before the router ever resolved a route.
    let hasNavigated = false

    // --- Router hook ---
    router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
      if (!isAuthenticated()) return

      hasNavigated = true
      const now = Date.now()
      const durationMs = from.path !== '/' || from.name ? now - lastNavTime : null

      const event: TrackingEvent = {
        event_type: 'page_view',
        route_path: to.path,
        route_name: (to.name as string) ?? null,
        metadata: from.path
          ? { from_path: from.path, from_name: (from.name as string) ?? null }
          : null,
        duration_ms: durationMs,
        session_id: sessionId,
        timestamp: new Date().toISOString(),
      }

      queue.push(event)

      // Auto-flush if queue is full
      if (queue.length >= MAX_QUEUE_SIZE) {
        flush(queue, getToken())
      }

      lastNavTime = now
    })

    // --- Periodic flush ---
    flushTimer = setInterval(() => {
      flush(queue, getToken())
    }, FLUSH_INTERVAL_MS)

    /* --- Click positions ---
     * Capture phase, so a handler that calls stopPropagation does not make the
     * click invisible. Passive: this must never delay the interaction it is
     * measuring.
     *
     * x is a FRACTION of viewport width, y is ABSOLUTE from the document top.
     * Horizontal layout scales with the window and vertical position does not,
     * so storing either the other way smears the heatmap across screen sizes.
     */
    document.addEventListener(
      'click',
      (e: MouseEvent) => {
        if (!isAuthenticated() || _clicks.length >= MAX_CLICKS_PER_FLUSH) return
        const vw = window.innerWidth
        const vh = window.innerHeight
        if (!vw || !vh) return // fired before layout; a 0 viewport is not data

        const { trackId, path } = elementReference(e.target as Element | null)
        if (!path) return

        _clicks.push({
          route_path: router.currentRoute.value.path,
          route_name: (router.currentRoute.value.name as string) ?? null,
          x_pct: Math.min(1, Math.max(0, e.clientX / vw)),
          y_px: Math.round(e.pageY),
          viewport_w: vw,
          viewport_h: vh,
          track_id: trackId,
          element_path: path,
          session_id: sessionId,
          timestamp: new Date().toISOString(),
        })

        if (_clicks.length >= MAX_CLICKS_PER_FLUSH) flush(queue, getToken())
      },
      { capture: true, passive: true },
    )

    // --- Flush on page unload (last page visit) ---
    window.addEventListener('beforeunload', () => {
      /*
       * GUARDED, and the guard is the whole point.
       *
       * Unguarded, this handler produced 149,865 of the 159,684 page views
       * recorded between 2026-02 and 2026-08 — 94% of them. They came from
       * sessions where the ops app reload-loops (~10 reloads/second, each page
       * living ~37ms), so every iteration emitted one page_view at `/` with no
       * route name. One session logged 15,047 events in 25 minutes.
       *
       * Two conditions now have to hold: the router must actually have
       * navigated at least once, and the page must have been alive long enough
       * to be a visit. A page that dies in milliseconds having never navigated
       * is a reload artifact, not a person.
       *
       * NOTE: the reload loop itself is a SEPARATE, still-live defect (first
       * seen 2026-05, 17 sessions in 2026-08). This guard stops it poisoning
       * the analytics; it does not stop the reloading.
       */
      const dwellMs = Date.now() - lastNavTime
      if (
        isAuthenticated() &&
        hasNavigated &&
        dwellMs >= MIN_UNLOAD_DWELL_MS &&
        router.currentRoute.value.path
      ) {
        const now = Date.now()
        queue.push({
          event_type: 'page_view',
          route_path: router.currentRoute.value.path,
          route_name: (router.currentRoute.value.name as string) ?? null,
          metadata: { unload: true },
          duration_ms: now - lastNavTime,
          session_id: sessionId,
          timestamp: new Date().toISOString(),
        })
      }
      flush(queue, getToken())

      if (flushTimer) {
        clearInterval(flushTimer)
      }
    })
  },
}
